import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import debounce from 'lodash/debounce';

import {
  Alert,
  ProgressBar,
  FormGroup,
  FormControl,
  HelpBlock,
  ControlLabel,
  Button,
  Row,
  Col,
} from 'react-bootstrap';
import Pagination from 'react-js-pagination';

import { BASE_URI, MISSING_POSITION_INTERVAL } from '../../settings';
import {
  fetchVariants,
  fetchVariantFeatureNames,
} from '../../actions/VariantActions';
import VariantTable from '../VariantTable';
import s from './VariantSearch.css';

const baseUrl = `${BASE_URI}variants/?`;
const defaultParams = 'rsid__isempty=False';

/*
 * General component for searching for and selecting a variant
 */
const VariantSearch = ({
  onChange,
  onSubmit,
  searchHelp,
  searchParamsProp,
  inputText,
  staticParams,
  showFeatures,
}) => {
  const dispatch = useDispatch();
  const variants = useSelector(state => state.variants);
  const featureNames = useSelector(state => state.variantFeatureNames);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentParams, setCurrentParams] = useState(staticParams);
  const [currentSearchUrl, setCurrentSearchUrl] = useState(
    `${baseUrl}${staticParams}&page=${1}`,
  );
  const [filteredData, setFilteredData] = useState([]);
  const [apiSearch, setApiSearch] = useState(null);
  const [searchFormText, setSearchFormText] = useState(''); // Debounced
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFeatureNames, setIsLoadingFeatureNames] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchValue = useCallback(
    debounce(value => {
      setSearchFormText(value);
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedSetSearchValue(searchFormText);
  }, [debouncedSetSearchValue, searchFormText]);

  useEffect(() => {
    let tmpParams = staticParams ? `${staticParams}` : '';
    tmpParams += searchParamsProp ? `&${searchParamsProp}` : '';
    setCurrentPage(1);
    setCurrentParams(tmpParams);
  }, [searchParamsProp]);

  useEffect(() => {
    // Happens when the examples from ExpectoLandingPage are clicked
    if (inputText !== apiSearch) {
      setSearchFormText(inputText);
      setApiSearch(inputText);
    }
  }, [inputText]);

  useEffect(() => {
    if (apiSearch === null) return;
    let tmpParams = staticParams ? `${staticParams}` : '';
    if (apiSearch !== '') {
      // Search by RsID by rsxxxxxxxx format
      if (/^rs/.test(apiSearch)) {
        tmpParams += `&rsid__startswith=${apiSearch}`;
        // Search by Chromosome and Position with chrx:xxxxxxxx format
      } else if (/^chr[0-9xyXY]{1,2}:/.test(apiSearch)) {
        const apiSearchOutput = apiSearch.split(':');
        if (apiSearchOutput.length > 1) {
          tmpParams += `&chromosome=${apiSearchOutput[0]}&position__startswith=${apiSearchOutput[1]}`;
        }
      } else if (showFeatures) {
        tmpParams += `&variantfeature__value__istartswith=${apiSearch}`;
      } else {
        // Default is set to search by Gene standard name
        tmpParams += `&gene__standard_name__istartswith=${apiSearch}`;
      }
    } else if (!staticParams) {
      // ASD VariantSearch uses hard coded featureName and staticParams
      tmpParams = featureNames.length ? searchParamsProp : defaultParams;
    }

    setCurrentPage(1);
    setCurrentParams(tmpParams);
  }, [apiSearch]);

  useEffect(() => {
    setCurrentSearchUrl(`${baseUrl}${currentParams}&page=${currentPage}`);
  }, [currentPage, currentParams]);

  // Format the data to the format we need.
  useEffect(() => {
    if (variants.results) {
      const mapFn = x => ({
        rsid: x.rsid,
        gene: x.gene ? x.gene.standard_name : '',
        position: x.position,
        chromosome: x.chromosome,
        gene_entrez: x.gene ? x.gene.entrez : '',
        ref_allele: x.ref_allele,
        alt_allele: x.alt_allele,
        features: x.features,
      });
      setFilteredData(variants.results.map(mapFn));
      setIsLoading(false);
    }

    // If the search result returns nothing and the search format is based
    // on chr and position then run another query checking for the range
    // of positions
    if (
      variants.count === 0 &&
      currentParams.search('position__startswith') >= 0
    ) {
      let searchUrl = `${baseUrl}${staticParams}&page=${currentPage}`;
      const apiSearchOutput = apiSearch.split(':');
      if (apiSearchOutput.length > 1) {
        const min =
          parseInt(apiSearchOutput[1], 10) - MISSING_POSITION_INTERVAL;
        const max =
          parseInt(apiSearchOutput[1], 10) + MISSING_POSITION_INTERVAL;
        searchUrl += `&chromosome=${apiSearchOutput[0]}&position__gte=${min}&position__lte=${max}`;
      }
      dispatch(fetchVariants(searchUrl));
    }
  }, [variants]);

  useEffect(() => {
    setIsLoading(true);
    // Get default searchurl using the default page and search
    dispatch(fetchVariants(currentSearchUrl));
  }, [currentSearchUrl]);

  useEffect(() => {
    if (showFeatures) {
      setIsLoadingFeatureNames(true);
      dispatch(fetchVariantFeatureNames(showFeatures)).then(() => {
        setIsLoadingFeatureNames(false);
      });
    }
  }, [showFeatures]);

  // Run this when submit or enter is pressed
  const onSearchSubmit = e => {
    // Get search results from inside the form
    setCurrentPage(1);
    setApiSearch(e.target.elements.search.value);
    e.preventDefault();
  };

  const progress = (
    <ProgressBar active striped label="Loading search results" now={100} />
  );

  const isEmpty = filteredData.length === 0;
  const shouldWaitForFeatures = showFeatures && isLoadingFeatureNames;

  // Create the pagination object that will be used
  const itemsPerPage = 20;
  const paginate =
    variants.count > itemsPerPage ? (
      <div
        style={
          !isLoading && !shouldWaitForFeatures && !isEmpty
            ? { textAlign: 'center' }
            : { display: 'none' }
        }
      >
        <Pagination
          activePage={currentPage}
          totalItemsCount={variants.count}
          itemsCountPerPage={itemsPerPage}
          onChange={setCurrentPage}
          pageRangeDisplayed={10}
        />
      </div>
    ) : null;

  let errMessage = ` No significant predictions for ${apiSearch} found`;
  if (apiSearch && apiSearch.match(/chr\d+/g) && !apiSearch.match(/:/g))
    errMessage = ` Search terms using chromosome alone must include a colon ':'`;

  const errAlert = (
    <Alert bsStyle="warning">
      <i className="fa fa-exclamation-triangle" />
      {errMessage}
    </Alert>
  );

  return (
    <div className="centerBlock">
      <br />
      <div style={{ margin: '0 auto' }}>
        <form onSubmit={onSearchSubmit}>
          <FormGroup role="form">
            <ControlLabel>{searchHelp}</ControlLabel>
            <Row>
              <Col xs={12}>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexGrow: 1 }}>
                    <FormControl
                      type="text"
                      name="search"
                      onChange={e => {
                        if (onChange) onChange(e);
                        setSearchFormText(e.target.value);
                      }}
                      value={searchFormText || ''}
                    />
                  </div>
                  {onChange ? null : (
                    <div>
                      <Button type="submit">Search</Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            <HelpBlock>
              {!showFeatures
                ? 'Search by single rsID, gene symbol, or chromosome position (chr1:877047)'
                : 'Search any column or a specific chomosome position (chr1:877047)'}
            </HelpBlock>
          </FormGroup>
        </form>
      </div>

      {paginate}

      {!isLoading && !isEmpty && !shouldWaitForFeatures ? (
        <div>
          <div style={{ textAlign: 'right' }}>
            {apiSearch?.length ? (
              <div style={{ display: 'inline', marginRight: '10px' }}>
                <span style={{ fontWeight: 'bold' }}>Search term: </span>
                <span>{apiSearch}</span>
              </div>
            ) : null}
            <span style={{ fontWeight: 'bold' }}>Results: </span>
            <span>{variants.count}</span>
          </div>
          <VariantTable
            variants={filteredData}
            showFeatures={showFeatures}
            featureNames={featureNames}
            onSubmit={onSubmit}
          />
        </div>
      ) : (
        <div>{!isLoading && !shouldWaitForFeatures ? errAlert : progress}</div>
      )}
      {paginate}
    </div>
  );
};

VariantSearch.propTypes = {
  searchParamsProp: PropTypes.string,
  staticParams: PropTypes.string,
  inputText: PropTypes.string,
  variants: PropTypes.shape({
    next: PropTypes.string,
    previous: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    results: PropTypes.array,
  }),
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  showFeatures: PropTypes.string,
  searchHelp: PropTypes.string,
};

VariantSearch.defaultProps = {
  onSubmit: null,
  onChange: null,
  searchHelp: '',
  inputText: null,
  searchParamsProp: null,
  showFeatures: null,
  staticParams: null,
  variants: null,
};

export default withStyles(s)(VariantSearch);
